import React, { useState, useEffect } from 'react';
import {
    History,
    Search,
    Filter,
    User,
    Calendar,
    Info,
    ArrowRight,
    Clock,
    AlertCircle,
    Download
} from 'lucide-react';
import {
    Card,
    Badge,
    Modal,
    Input,
    Spinner,
    Alert,
    ButtonSecondary
} from '../../components/ComponentLibrary';
import { getActivityLogs, getAllUsers } from '../../services/api';
import './Admin.css';

export default function AdminHistory() {
    const [logs, setLogs] = useState([]);
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedLog, setSelectedLog] = useState(null);

    // Filters
    const [filters, setFilters] = useState({
        actionType: '',
        performedBy: '',
        startDate: '',
        endDate: ''
    });

    const fetchLogs = async () => {
        try {
            setLoading(true);
            const response = await getActivityLogs(filters);
            setLogs(response.data);
            setError(null);
        } catch (err) {
            setError('Failed to load activity logs.');
        } finally {
            setLoading(false);
        }
    };

    const fetchAdmins = async () => {
        try {
            const users = await getAllUsers({ role: 'admin' });
            setAdmins(users);
        } catch (err) { }
    };

    useEffect(() => {
        fetchLogs();
    }, [filters]);

    useEffect(() => {
        fetchAdmins();
    }, []);

    const getActionBadge = (type) => {
        if (type.includes('ADDED') || type.includes('CREATED')) return 'success';
        if (type.includes('UPDATED') || type.includes('CHANGED')) return 'warning';
        if (type.includes('DELETED')) return 'error';
        return 'ocean';
    };

    const formatActionType = (type) => {
        return type.replace(/_/g, ' ');
    };

    return (
        <div className="admin-history-page">
            <header className="admin-page-header">
                <div className="admin-page-header-text">
                    <h1>System Audit Logs</h1>
                    <p>Transparent record of all administrative and financial actions</p>
                </div>
            </header>

            <Card className="filter-card mb-4" hoverable={false}>
                <div className="filter-grid-history">
                    <div className="filter-col">
                        <label className="cw-input-label">Action Type</label>
                        <select
                            className="cw-input"
                            value={filters.actionType}
                            onChange={(e) => setFilters({ ...filters, actionType: e.target.value })}
                        >
                            <option value="">All Actions</option>
                            <option value="PAYMENT_ADDED">Payment Added</option>
                            <option value="PAYMENT_UPDATED">Payment Updated</option>
                            <option value="PAYMENT_DELETED">Payment Deleted</option>
                            <option value="STUDENT_CREATED">Student Created</option>
                            <option value="FEES_CHANGED">Fees Changed</option>
                            <option value="GROUP_CHANGED">Group Changed</option>
                            <option value="STUDENT_UPDATED">Student Updated</option>
                        </select>
                    </div>

                    <div className="filter-col">
                        <label className="cw-input-label">Performed By</label>
                        <select
                            className="cw-input"
                            value={filters.performedBy}
                            onChange={(e) => setFilters({ ...filters, performedBy: e.target.value })}
                        >
                            <option value="">All Admins</option>
                            {admins.map(a => (
                                <option key={a._id} value={a._id}>{a.fullName}</option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-col">
                        <label className="cw-input-label">Start Date</label>
                        <Input
                            type="date"
                            value={filters.startDate}
                            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                        />
                    </div>

                    <div className="filter-col">
                        <label className="cw-input-label">End Date</label>
                        <Input
                            type="date"
                            value={filters.endDate}
                            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                        />
                    </div>
                </div>
            </Card>

            <div className="admin-table-section-card">
                {loading ? (
                    <div className="loading-container p-5 text-center">
                        <Spinner size="lg" />
                        <p className="mt-2 text-muted">Retrieving audit trail...</p>
                    </div>
                ) : logs.length === 0 ? (
                    <div className="empty-history p-5 text-center">
                        <History size={48} className="text-muted opacity-20 mb-3" />
                        <p>No activity logs found matching your filters.</p>
                    </div>
                ) : (
                    <div className="admin-table-wrapper">
                        <table className="admin-standard-table">
                            <thead>
                                <tr>
                                    <th>Timestamp</th>
                                    <th>Admin</th>
                                    <th>Action Type</th>
                                    <th>Description</th>
                                    <th style={{ textAlign: 'right' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map(log => (
                                    <tr key={log._id}>
                                        <td>
                                            <div className="time-col">
                                                <div className="date-txt">{new Date(log.createdAt).toLocaleDateString()}</div>
                                                <div className="time-txt"><Clock size={12} /> {new Date(log.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="admin-cell">
                                                <div className="admin-avatar-xs">{log.performedByName[0]}</div>
                                                <span>{log.performedByName}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <Badge variant={getActionBadge(log.actionType)}>
                                                {formatActionType(log.actionType)}
                                            </Badge>
                                        </td>
                                        <td>
                                            <div className="description-cell" title={log.description}>
                                                {log.description}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="action-cell-right">
                                                <button
                                                    className="btn-action-sm"
                                                    onClick={() => setSelectedLog(log)}
                                                >
                                                    <Info size={14} /> Details
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Log Details Modal */}
            <Modal
                isOpen={!!selectedLog}
                onClose={() => setSelectedLog(null)}
                title="System Action Details"
                size="lg"
            >
                {selectedLog && (
                    <div className="log-details-container">
                        <div className="log-header-info">
                            <Badge variant={getActionBadge(selectedLog.actionType)} className="mb-2">
                                {formatActionType(selectedLog.actionType)}
                            </Badge>
                            <h2>{selectedLog.description}</h2>
                        </div>

                        <div className="log-meta-grid">
                            <div className="meta-item">
                                <span className="meta-label">Performed By</span>
                                <span className="meta-value">{selectedLog.performedByName}</span>
                            </div>
                            <div className="meta-item">
                                <span className="meta-label">Date & Time</span>
                                <span className="meta-value">{new Date(selectedLog.createdAt).toLocaleString()}</span>
                            </div>
                            <div className="meta-item">
                                <span className="meta-label">Target Entity</span>
                                <span className="meta-value">{selectedLog.entityType} ({selectedLog.entityId})</span>
                            </div>
                            {selectedLog.studentId && (
                                <div className="meta-item">
                                    <span className="meta-label">Related Student</span>
                                    <span className="meta-value">{selectedLog.studentId?.fullName || 'See ID'}</span>
                                </div>
                            )}
                        </div>

                        <div className="state-comparison">
                            <div className="state-box old">
                                <h3><AlertCircle size={16} /> Previous State</h3>
                                <pre>{JSON.stringify(selectedLog.oldValue, null, 2)}</pre>
                            </div>
                            <div className="state-arrow">
                                <ArrowRight size={24} />
                            </div>
                            <div className="state-box new">
                                <h3><AlertCircle size={16} /> New State</h3>
                                <pre>{JSON.stringify(selectedLog.newValue, null, 2)}</pre>
                            </div>
                        </div>

                        <div className="modal-footer-actions">
                            <ButtonSecondary onClick={() => setSelectedLog(null)}>Close View</ButtonSecondary>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}
